import { Controller } from "@hotwired/stimulus"
import { createSuggestionRenderer } from "../lib/suggestion_popup"

// FD-908: the roadmap item page's "add a work item" picker.
//
// It replaced a free-text field whose value the server cut at the last "-" and looked up as a
// sequence number in THIS project, so another project's key, or any string ending in a
// number, silently linked the wrong item. Here the text the user types is only ever a SEARCH:
// it has no `name`, so it is never submitted. What the form sends is the chosen item's id, in
// the hidden `field` target, and choosing is what submits it.
//
// Results come from the server (MilestonesController#addable_items, JSON), one request per
// pause in typing, because a project can hold thousands of items. The popup is the shared
// lib/suggestion_popup shell, as with the owner and "@" pickers, so it mounts, positions,
// survives a morph and handles the arrow keys the same way they do.
const DEBOUNCE_MS = 120

export default class extends Controller {
  static targets = [ "input", "field" ]
  static values = { url: String }

  connect() {
    this.onFocus = () => this.refresh()
    this.onInput = () => this.refresh()
    this.onKeyDown = (event) => this.handleKeyDown(event)
    this.onBlur = () => this.close()

    const input = this.inputTarget
    input.addEventListener("focus", this.onFocus)
    input.addEventListener("input", this.onInput)
    input.addEventListener("keydown", this.onKeyDown)
    input.addEventListener("blur", this.onBlur)
  }

  disconnect() {
    const input = this.inputTarget
    input.removeEventListener("focus", this.onFocus)
    input.removeEventListener("input", this.onInput)
    input.removeEventListener("keydown", this.onKeyDown)
    input.removeEventListener("blur", this.onBlur)
    this.close()
  }

  // A morph can delete the body-mounted popup while this controller survives, so ask the
  // shell whether it is still there rather than trusting a stored flag.
  activeRenderer() {
    if (this.renderer && !this.renderer.isLive()) this.close()
    return this.renderer
  }

  refresh() {
    // A fresh query invalidates any earlier pick: the hidden id must only ever be the item
    // the user chose from THIS list.
    this.fieldTarget.value = ""
    const query = this.inputTarget.value.trim()

    if (!this.activeRenderer()) {
      this.renderer = createSuggestionRenderer({
        prefix: "fd-mention",
        emptyText: "No matching work items",
        renderItem: renderWorkItemRow,
      })
      this.items = []
      this.renderer.onStart(this.props([], true))
    }

    // Until this query's results land, the list on screen belongs to the PREVIOUS query.
    // Enter is held rather than applied to it (see handleKeyDown), or pasting "WEB-12" and
    // pressing Enter at once would add whatever the last search had on top.
    this.pending = true
    this.chooseOnArrival = false
    const generation = (this.generation = (this.generation || 0) + 1)
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.fetchItems(query, generation), DEBOUNCE_MS)
  }

  async fetchItems(query, generation) {
    this.abort?.abort()
    this.abort = new AbortController()
    let items = []
    try {
      const url = new URL(this.urlValue, window.location.origin)
      if (query) url.searchParams.set("q", query)
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.abort.signal,
      })
      if (!res.ok) throw new Error(res.status)
      const list = await res.json()
      items = Array.isArray(list) ? list : []
    } catch (error) {
      if (error.name === "AbortError") return
      items = [] // an outage or an expired session: offer nothing rather than break typing
    }
    if (generation !== this.generation || !this.activeRenderer()) return

    this.pending = false
    this.items = items
    this.renderer.onUpdate(this.props(items, false))
    if (this.chooseOnArrival && items.length) this.choose(items[0])
    this.chooseOnArrival = false
  }

  props(items, loading) {
    return {
      items,
      loading,
      command: (item) => this.choose(item),
      element: this.inputTarget,
      clientRect: () => this.inputTarget.getBoundingClientRect(),
    }
  }

  close() {
    clearTimeout(this.timer)
    this.abort?.abort()
    this.pending = false
    this.chooseOnArrival = false
    this.generation = (this.generation || 0) + 1 // orphan any in-flight response
    if (!this.renderer) return
    this.renderer.onExit()
    this.renderer = null
  }

  // Choosing IS the submit.
  choose(item) {
    this.fieldTarget.value = item.id
    this.inputTarget.value = `${item.key} ${item.title}`
    this.close()
    this.element.requestSubmit()
  }

  handleKeyDown(event) {
    const renderer = this.activeRenderer()

    // Enter on an empty field means nothing: an unfiltered list is not a selection, and the
    // form has nothing to send. Never let it reach the form.
    if (event.key === "Enter" && !this.inputTarget.value.trim()) {
      event.preventDefault()
      return this.close()
    }
    if (!renderer) {
      // No list open (it was dismissed): Enter would submit an empty id, so reopen instead.
      if (event.key === "Enter") { event.preventDefault(); this.refresh() }
      return
    }
    if (event.key === "Tab") return this.close()

    if (this.pending && event.key === "Enter") {
      event.preventDefault()
      this.chooseOnArrival = true
      return
    }

    if (!renderer.onKeyDown({ event })) return
    // Ours to do on a plain field: Enter would submit the form, Escape would close an
    // enclosing dialog, and the arrows would move the caret.
    event.preventDefault()
    event.stopPropagation()
    if (event.key === "Escape") this.close()
  }
}

// A result row: state dot, key, title, and the state's name.
function renderWorkItemRow(item, button) {
  const dot = document.createElement("span")
  dot.className = "inline-block shrink-0 size-2 rounded-full"
  dot.style.background = item.state?.color || "#9ca3af"
  const key = document.createElement("span")
  key.className = "shrink-0 text-[11px] text-muted"
  key.textContent = item.key
  const title = document.createElement("span")
  title.className = "fd-mention-name min-w-0 flex-1"
  title.textContent = item.title
  button.append(dot, key, title)
  if (item.state?.name) {
    const state = document.createElement("span")
    state.className = "fd-mention-email"
    state.textContent = item.state.name
    button.append(state)
  }
  button.title = `${item.key} ${item.title}`
}
